import React, { useState, useMemo } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Table from "../../common/Table";

function ViewAssignedVotersTab({ voters = [], precinctName }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [votersPerPage, setVotersPerPage] = useState(5);

  // Normalize incoming voter objects to a consistent shape for display/search
  const voterList = useMemo(() => {
    if (!Array.isArray(voters)) return [];
    return voters.map((v, idx) => {
      const resident = v.resident || {};
      const firstName = v.first_name || v.firstName || "";
      const lastName = v.last_name || v.lastName || "";
      const name =
        resident.full_name ||
        v.name ||
        v.full_name ||
        `${firstName} ${lastName}`.trim();
      const id =
        v.voters_id_no ||
        v.voter_id ||
        resident.resident_id ||
        v.id ||
        String(idx);
      const phone =
        resident.contact_number || v.phone || v.mobile || v.contact || "";
      const rawStatus = v.status || "Active";
      const status =
        typeof rawStatus === "string"
          ? rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1)
          : "Active";
      const precinct = v.precinct || v.precinct_name || undefined;
      return { id: String(id), name: name || "Unknown", phone, status, precinct };
    });
  }, [voters]);

  const filteredVoters = useMemo(() => {
    return voterList
      .filter((v) => v.name && v.id)
      .filter(
        (voter) =>
          (!precinctName || voter.precinct === precinctName) &&
          (voter.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            voter.id.includes(searchTerm))
      );
  }, [voterList, searchTerm, precinctName]);

  const totalPages = Math.ceil(filteredVoters.length / votersPerPage);
  const indexOfLastVoter = currentPage * votersPerPage;
  const indexOfFirstVoter = indexOfLastVoter - votersPerPage;
  const currentVoters = filteredVoters.slice(indexOfFirstVoter, indexOfLastVoter);

  const columns = [
    {
      key: "voter",
      label: "VOTER",
      render: (_, voter) => (
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-lg">
            {voter.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)}
          </div>
          <div>
            <p className="font-medium text-gray-800">{voter.name}</p>
            <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
              <span className="bg-blue-100 text-blue-700 font-semibold px-2 py-0.5 rounded">
                {voter.id}
              </span>
              <span>{voter.phone}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: "STATUS",
      render: (_, voter) => (
        <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm">
          {voter.status || "Active"}
        </span>
      ),
    },
  ];

  return (
    <div className="p-6">
      {/* Search Bar */}
      <div className="relative w-full mb-4">
        <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search assigned voter by name or resident ID..."
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Scrollable Voter List */}
      <div className="max-h-96 overflow-y-auto border border-gray-300 rounded p-2 space-y-2">
        {currentVoters.length > 0 ? (
          <Table
            noContainer={true}
            columns={columns}
            data={currentVoters}
            getRowKey={(voter) => voter.id}
            pagination={
              filteredVoters.length > 0
                ? {
                    currentPage,
                    lastPage: totalPages,
                    perPage: votersPerPage,
                    total: filteredVoters.length,
                  }
                : null
            }
            onPageChange={(page, newPerPage = votersPerPage) => {
              setCurrentPage(page);
              if (newPerPage !== votersPerPage) {
                setVotersPerPage(newPerPage);
                setCurrentPage(1);
              }
            }}
            paginationProps={{
              onPerPageChange: (value) => {
                setVotersPerPage(value);
                setCurrentPage(1);
              },
              showSizeSelector: true,
            }}
          />
        ) : (
          <p className="text-gray-500 text-center py-4">
            No voters found for this precinct.
          </p>
        )}
      </div>
    </div>
  );
}

export default ViewAssignedVotersTab;
